import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import { TwScope } from "../../../components/ui/TwScope";
// The register, edit and details flows have all been rebuilt in Tailwind —
// see ../ingredientRegister/IngredientRegisterModal.tsx,
// ../ingredientEdit/IngredientEditModal.tsx and
// ../ingredientDetails/IngredientDetailsModal.tsx.
import IngredientDetailsModal from "../ingredientDetails/IngredientDetailsModal";
import IngredientEditModal from "../ingredientEdit/IngredientEditModal";
import IngredientRegisterModal from "../ingredientRegister/IngredientRegisterModal";
import IngredientListHeader from "./components/IngredientListHeader";
import IngredientListPagination from "./components/IngredientListPagination";
import IngredientListToolbar from "./components/IngredientListToolbar";
import IngredientTable from "./components/IngredientTable";
import LoadingOverlay from "./components/LoadingOverlay";
import { useIngredientListViewModel } from "./hooks/useIngredientListViewModel";

/**
 * Tailwind rebuild of the LCA Ingredient grid, matching the validated
 * Lovable prototype and styled after the Recipe Simulator list screen
 * (src/screens/recipe_simulator/RecipeSimulatorNewListView.tsx) — same
 * `TwScope` page shell, same table/search/badge/action-button patterns.
 * All state/data-fetching lives in `useIngredientListViewModel`; this
 * component only composes presentation.
 *
 * Gated behind `ENABLE_LCA_INGREDIENT_UI_REDESIGN` — see
 * src/routes/ingredientRoutes.tsx and src/screens/ingredients/BillOfMaterial.tsx
 * for the switches against the untouched legacy `IngredientList`
 * (src/screens/ingredients/ingredientList/IngredientList.tsx).
 */
const IngredientListNew = () => {
  const viewModel = useIngredientListViewModel();

  return (
    <TwScope className="tw-min-h-full tw-bg-white tw-p-6">
      <IngredientListHeader onRegisterClick={viewModel.openRegisterModal} />

      <IngredientListToolbar
        searchKey={viewModel.searchKey}
        onSearchChange={viewModel.setSearchKey}
        refetchWithFilters={viewModel.refetchWithFilters}
      />

      <IngredientTable
        rows={viewModel.rows}
        isLoading={viewModel.isRowsLoading}
        isUserSupplier={viewModel.isUserSupplier}
        onView={viewModel.openDetailsModal}
        onEdit={viewModel.openEditModal}
        onDelete={viewModel.openDeleteConfirm}
      />

      <IngredientListPagination
        totalCount={viewModel.totalCount}
        pageNumber={viewModel.pageNumber}
        totalPages={viewModel.totalPages}
        onPageChange={viewModel.handlePagination}
      />

      {viewModel.visibleRegisterModal ? (
        <IngredientRegisterModal
          visibleModal={viewModel.visibleRegisterModal}
          setVisibleModal={viewModel.closeRegisterModal}
          ingredientList={viewModel.ingredientListResult}
          refetchIngredients={viewModel.refetchIngredients}
        />
      ) : null}

      {viewModel.visibleEditModal ? (
        <IngredientEditModal
          ingredientIdToUpdate={viewModel.editIngredientData}
          visibleModal={viewModel.visibleEditModal}
          setVisibleModal={viewModel.closeEditModal}
          refetchIngredients={viewModel.refetchIngredients}
          isEnableEnvironmentData={true}
          isEnableIngredientMapping={true}
        />
      ) : null}

      {viewModel.visibleDetailsModal ? (
        <IngredientDetailsModal
          visibleModal={viewModel.visibleDetailsModal}
          setVisibleModal={viewModel.closeDetailsModal}
          ingredientDetails={viewModel.viewIngredientData}
          navigateIngredientEdit={viewModel.navigateDetailsToEdit}
        />
      ) : null}

      <ConfirmModal
        viewModal={viewModel.visibleDeleteConfirm}
        closeModal={viewModel.closeDeleteConfirm}
        cancel={viewModel.closeDeleteConfirm}
        approve={viewModel.confirmDelete}
        title="Delete Ingredient Data"
        subTitle="Are you sure you want to remove this ingredient?"
      />

      {viewModel.isMutationInFlight ? <LoadingOverlay /> : null}
    </TwScope>
  );
};

export default IngredientListNew;
